import classNames from 'classnames';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
} from 'components/organisms/interactiveTime/hooks';
import { useTranslation } from 'react-i18next';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import {
  hasStatusForDisableCcmModule,
  hasStatusForDisableRpmModule,
} from 'infrastructure/functions/status-check';
import { DateFormatter } from 'infrastructure/functions';

import s from './styles.module.scss';
import ModuleTime from './module-time';
import InfoBlock from './info-block';

import type { CcmStatuses, RpmStatuses } from 'infrastructure/enums';
import type { FC } from 'react';

const ACTION_TIME_QUOTA_55 = 55 * 60;

const checkActionTimeQuota = (totalTime?: number) => {
  if (!totalTime) return false;

  return totalTime >= ACTION_TIME_QUOTA_55;
};

const checkProgramType = (rpmStatus?: RpmStatuses, ccmStatus?: CcmStatuses) => {
  let rpm = false;
  let ccm = false;

  if (rpmStatus) rpm = !hasStatusForDisableRpmModule(rpmStatus);
  if (ccmStatus) ccm = !hasStatusForDisableCcmModule(ccmStatus);

  const statusMap = {
    RPM: rpm,
    CCM: ccm,
  };

  return Object.entries(statusMap)
    .filter(([, value]) => value)
    .map(([key]) => key)
    .join('/');
};

interface PatientInfoProps {
  patientGuid: string;
}

const PatientInfo: FC<PatientInfoProps> = ({ patientGuid }) => {
  const { ccmInteractionTime, isLoading: isCcmLoading } =
    useGetCcmInteractionTime({
      patientGuid,
      enabled: true,
    });
  const { rpmInteractionTime, isLoading: isRpmLoading } =
    useGetRpmInteractionTime({
      patientGuid,
      enabled: true,
    });
  const { patientInfo, loading: infoLoading } = usePatientInfo({ patientGuid });
  const { t } = useTranslation();

  const today = new Date();
  const monthlyInteractionStatus = patientInfo?.interactionStatus
    ? t('controls.yes')
    : t('controls.no');
  const followUpStatus = patientInfo?.ccmInteractionStatus
    ? t('controls.yes')
    : t('controls.no');
  const programType = checkProgramType(
    patientInfo?.status,
    patientInfo?.ccmStatus,
  );

  const rpmQuotaAccent = checkActionTimeQuota(rpmInteractionTime?.totalTime);

  return (
    <BaseSpinWrapper spinning={isCcmLoading || isRpmLoading || infoLoading}>
      <div className={s.time}>
        <h4 className={classNames(s.title, s['time-title'])}>
          {t('labels.patientInfo')}
        </h4>
        <div data-time>
          <div className={s['time-wrapper']}>
            <div className={s.divider} />
            <ModuleTime
              clinicTime={rpmInteractionTime?.clinicTime}
              totalTime={rpmInteractionTime?.totalTime}
              module={ModuleType.RPM}
              accent={rpmQuotaAccent}
            />
            <div
              className={classNames(s['time-divider'], {
                [s.accent]: rpmQuotaAccent,
              })}
            >
              <div className={s.divider} />
              <div className={s['time-month']}>
                {DateFormatter({ date: today, format: DateFormats.MMMM })}
              </div>
              <div className={s.divider} />
            </div>
          </div>
          <ModuleTime
            clinicTime={ccmInteractionTime?.clinicTime}
            totalTime={ccmInteractionTime?.totalTime}
            module={ModuleType.CCM}
          />
        </div>
        <div className={s.divider} data-middle />
        <div data-info>
          <InfoBlock label={t('labels.programType')} value={programType} />
          <div className={s.divider} />
          <InfoBlock
            label={t('links.monthlyInteractionStatus')}
            value={monthlyInteractionStatus}
          />
          <div className={s.divider} />
          <InfoBlock
            label={t('labels.followUpAppointment')}
            value={followUpStatus}
          />
        </div>
      </div>
    </BaseSpinWrapper>
  );
};
export default PatientInfo;
